import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowDownBigSmall,
    faArrowUpBigSmall,
    faGripDotsVertical,
    faPlus,
    faSortUpDown,
    faTableColumns,
    faXmark,
} from "@fortawesome/sharp-light-svg-icons";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { PrimaryButton, SecondaryButton } from "../../ui/Buttons";
import { CustomSwitch } from "../../ui/CustomSwitch";
import { Modal } from "../../ui/Modals";
import { columnLabel } from "./utils";

type ColumnVisibilityDef = {
    id: string;
    name: string;
}[];

interface TableSettingsModalProps {
    columnVisibility: {
        [k: string]: boolean;
    };
    setColumnVisibility: (newStates: { [k: string]: boolean }) => void;
    configurableColumns: ColumnVisibilityDef;
}

export const TableSettingsModal = (props: TableSettingsModalProps) => {
    const [showTableSettings, setShowTableSettings] = useState(false);

    return (
        <>
            <SecondaryButton
                onClick={() => setShowTableSettings(true)}
                variant="sm"
            >
                <FontAwesomeIcon icon={faTableColumns} className="w-4" />
                Configure table
            </SecondaryButton>

            {/* Table settings modal */}
            <Modal
                visible={showTableSettings}
                size="sm"
                onClose={() => setShowTableSettings(false)}
            >
                <h1 className="text-base font-bold mt-1">Table settings</h1>
                <p className="text-sm text-neutral-500">
                    Toggle column visibility for the infrastructure table.
                </p>

                <div className="text-sm mt-6 flex flex-col divide-y max-h-[300px] overflow-auto">
                    {props.configurableColumns.map((c) => (
                        <div
                            className="flex items-center gap-4 py-3"
                            key={c.id}
                        >
                            <CustomSwitch
                                checked={
                                    !(c.id in (props.columnVisibility ?? {})) ||
                                    props.columnVisibility[c.id]
                                }
                                onChange={(v) => {
                                    const newValues = {
                                        ...(props.columnVisibility ?? {}),
                                    };
                                    newValues[c.id] = v;
                                    props.setColumnVisibility(newValues);
                                }}
                                size="xs"
                            />
                            {c.name}
                        </div>
                    ))}
                </div>
            </Modal>
        </>
    );
};

interface SortableColumn {
    id: string;
    name: string;
}

interface TableSortModalProps<T> {
    columns: ColumnDef<T, any>[];
    sortingState?: SortingState;
    setSortingState: (newState: SortingState) => void;
}

export const TableSortModal = <T,>(props: TableSortModalProps<T>) => {
    const [showSortModal, setShowSortModal] = useState(false);
    const [draft, setDraft] = useState<SortingState>(props.sortingState ?? []);
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    // Resolve sortable columns from the column definitions.
    const sortableColumns = useMemo(() => {
        const sortable: SortableColumn[] = [];
        for (const col of props.columns) {
            if (col.enableSorting === false) {
                continue;
            }
            const id = col.id ?? (col as any).accessorKey;
            if (!id) {
                continue;
            }
            sortable.push({ id, name: columnLabel(col, id) });
        }
        return sortable;
    }, [props.columns]);

    // Seed the draft from the current sorting state whenever the modal opens.
    useEffect(() => {
        if (showSortModal) {
            setDraft(props.sortingState ?? []);
            setDragIndex(null);
            setDragOverIndex(null);
        }
    }, [showSortModal, props.sortingState]);

    const applyAndClose = () => {
        props.setSortingState(draft);
        setShowSortModal(false);
    };

    const cancelAndClose = () => {
        setShowSortModal(false);
    };

    const addLevel = () => {
        setDraft((d) => {
            const usedIds = new Set(d.map((s) => s.id));
            const next =
                sortableColumns.find((c) => !usedIds.has(c.id))?.id ??
                (d.length > 0 ? d[d.length - 1].id : "");
            return [...d, { id: next, desc: false }];
        });
    };

    const removeLevel = (index: number) => {
        setDraft((d) => d.filter((_, i) => i !== index));
    };

    // Reorder levels via drag and drop, moving the dragged level to a new slot.
    const reorder = (from: number, to: number) => {
        if (from === to) {
            return;
        }
        setDraft((d) => {
            const next = [...d];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    const endDrag = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    const setLevelColumn = (index: number, id: string) => {
        setDraft((d) => d.map((s, i) => (i === index ? { ...s, id } : s)));
    };

    const setLevelDesc = (index: number, desc: boolean) => {
        setDraft((d) => d.map((s, i) => (i === index ? { ...s, desc } : s)));
    };

    return (
        <>
            <SecondaryButton
                onClick={() => setShowSortModal(true)}
                variant="sm"
            >
                <FontAwesomeIcon icon={faSortUpDown} className="w-3" />
                Sort
            </SecondaryButton>

            {/* Sort modal */}
            <Modal visible={showSortModal} size="md" onClose={cancelAndClose}>
                <h1 className="text-base font-bold mt-1">Sort table</h1>
                <p className="text-sm text-neutral-500">
                    Add levels to sort by multiple columns. The top level is
                    applied first.
                </p>

                {/* Sort levels grid */}
                <div className="mt-3 border border-ae-gray-250 rounded max-h-[300px] overflow-auto">
                    {/* Header row */}
                    <div className="flex items-center gap-3 px-3 py-2 bg-ae-slate-100 text-xs font-semibold text-ae-slate-600 sticky top-0">
                        <span className="w-4 shrink-0" />
                        <span className="w-16 shrink-0" />
                        <span className="flex-1">Column</span>
                        <span className="w-[168px] shrink-0">Order</span>
                        <span className="w-6 shrink-0" />
                    </div>

                    {draft.length === 0 ? (
                        <p className="text-sm text-neutral-400 text-center py-6">
                            No sort levels. Click "Add Level" to start.
                        </p>
                    ) : (
                        <div>
                            {draft.map((level, index) => {
                                const usedElsewhere = new Set(
                                    draft
                                        .filter((_, i) => i !== index)
                                        .map((s) => s.id),
                                );
                                const available = sortableColumns.filter(
                                    (c) => !usedElsewhere.has(c.id),
                                );
                                const isDragging = dragIndex === index;
                                const isDropTarget =
                                    dragOverIndex === index &&
                                    dragIndex !== null &&
                                    dragIndex !== index;
                                return (
                                    <div
                                        key={level.id || index}
                                        draggable
                                        onDragStart={(e) => {
                                            e.dataTransfer.effectAllowed =
                                                "move";
                                            e.dataTransfer.setData(
                                                "text/plain",
                                                String(index),
                                            );
                                            setDragIndex(index);
                                        }}
                                        onDragEnd={endDrag}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = "move";
                                            if (dragOverIndex !== index) {
                                                setDragOverIndex(index);
                                            }
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            if (dragIndex !== null) {
                                                reorder(dragIndex, index);
                                            }
                                            endDrag();
                                        }}
                                        className={`flex items-center gap-3 px-3 py-2 ${
                                            isDragging
                                                ? "opacity-40"
                                                : isDropTarget
                                                  ? "bg-ae-blue-50"
                                                  : "hover:bg-ae-gray-100"
                                        }`}
                                    >
                                        <span
                                            className="w-4 shrink-0 flex items-center justify-center text-neutral-400 cursor-grab active:cursor-grabbing"
                                            title="Drag to reorder"
                                        >
                                            <FontAwesomeIcon
                                                icon={faGripDotsVertical}
                                                className="w-3"
                                            />
                                        </span>
                                        <span className="w-16 shrink-0 text-sm text-ae-slate-600">
                                            {index === 0
                                                ? "Sort by"
                                                : "Then by"}
                                        </span>
                                        <select
                                            value={level.id}
                                            onChange={(e) =>
                                                setLevelColumn(
                                                    index,
                                                    e.target.value,
                                                )
                                            }
                                            className="h-8 text-sm px-2 py-0.5 flex-1 rounded border-ae-gray-250 focus:ring-ae-blue-550 focus:border-ae-blue-550"
                                        >
                                            {available.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="w-[168px] shrink-0 flex h-8 text-xs rounded border border-ae-gray-250 overflow-hidden">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setLevelDesc(index, false)
                                                }
                                                className={`flex-1 flex items-center justify-center gap-1.5 ${
                                                    !level.desc
                                                        ? "bg-ae-blue-550 text-white"
                                                        : "bg-white text-ae-blue-900 hover:bg-ae-gray-100"
                                                }`}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faArrowUpBigSmall}
                                                    className="w-3"
                                                />
                                                Asc
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setLevelDesc(index, true)
                                                }
                                                className={`flex-1 flex items-center justify-center gap-1.5 border-l border-ae-gray-250 ${
                                                    level.desc
                                                        ? "bg-ae-blue-550 text-white"
                                                        : "bg-white text-ae-blue-900 hover:bg-ae-gray-100"
                                                }`}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faArrowDownBigSmall}
                                                    className="w-3"
                                                />
                                                Desc
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeLevel(index)}
                                            className="w-6 shrink-0 flex items-center justify-center p-1 rounded text-ae-blue-900 hover:bg-ae-gray-100"
                                            title="Delete level"
                                        >
                                            <FontAwesomeIcon
                                                icon={faXmark}
                                                className="w-3"
                                            />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                {/* Toolbar */}
                <div className="mt-2 flex items-center gap-2">
                    <SecondaryButton
                        variant="sm"
                        onClick={addLevel}
                        disabled={draft.length >= sortableColumns.length}
                    >
                        <FontAwesomeIcon icon={faPlus} className="w-3" />
                        Add Level
                    </SecondaryButton>
                </div>

                <p className="text-xs text-neutral-400 mt-2">
                    Tip: SHIFT + click a column's sort icon in the table header
                    to quickly add multi-column sorting.
                </p>

                <div className="mt-2 flex justify-end gap-2">
                    <SecondaryButton variant="sm" onClick={cancelAndClose}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton variant="sm" onClick={applyAndClose}>
                        Apply
                    </PrimaryButton>
                </div>
            </Modal>
        </>
    );
};
